/**
 * Templates API service for CRUD operations on user templates.
 * Templates are stored in DynamoDB (Templates table).
 * Default templates are stored in a separate table (DefaultTemplates).
 */

import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

export interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  outputFormat: string;
  icon?: string;
  color?: string;
  createdAt: string;
  lastUpdated: string;
  createdBy: string;
  createdByEmail?: string;
  isFromDefault?: boolean;
  defaultTemplateId?: string;
}

export interface DefaultTemplateMetadata {
  templateId: string;
  templateName: string;
  templateStructure: string;
  description: string;
  outputFormat: string;
  icon?: string;
  color?: string;
  prompt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateResponse {
  metadata: TemplateMetadata;
  content: string;
}

export interface SaveTemplateRequest {
  name: string;
  description: string;
  content: string;
  outputFormat?: string;
  icon?: string;
  color?: string;
  prompt?: string;
}

export interface SaveDefaultTemplateRequest {
  templateName: string;
  description: string;
  templateStructure: string;
  outputFormat: string;
  color: string;
  icon: string;
  prompt: string;
}

export interface CopyDefaultsRequest {
  force?: boolean;
  templateIds?: string[];
}

export interface CopyDefaultsResponse {
  success: boolean;
  message: string;
  copied: number;
  templates: TemplateMetadata[];
}

class TemplatesApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'TemplatesApi');
  }

  // ============================================
  // User Templates (DynamoDB Templates table)
  // ============================================

  async listTemplates(): Promise<TemplateMetadata[]> {
    this.log.debug('Listing templates', { action: 'listTemplates' });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/templates`, { method: 'GET', headers });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to list templates', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list templates');
    }

    const data = await response.json();
    this.log.info('Templates listed', { action: 'listTemplates', count: data.templates?.length || 0 });
    return data.templates || [];
  }

  async getTemplate(templateId: string): Promise<TemplateResponse> {
    this.log.debug('Getting template', { action: 'getTemplate', templateId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/templates/${templateId}`, { method: 'GET', headers });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get template', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get template');
    }

    return response.json();
  }

  async createTemplate(request: SaveTemplateRequest): Promise<TemplateMetadata> {
    this.log.debug('Creating template', { action: 'createTemplate', name: request.name });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/templates`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to create template', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to create template');
    }

    const data = await response.json();
    this.log.info('Template created', { action: 'createTemplate', templateId: data.template?.id });
    return data.template;
  }

  async updateTemplate(templateId: string, request: SaveTemplateRequest): Promise<TemplateMetadata> {
    this.log.debug('Updating template', { action: 'updateTemplate', templateId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/templates/${templateId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to update template', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to update template');
    }

    const data = await response.json();
    return data.template;
  }

  async deleteTemplate(templateId: string): Promise<void> {
    this.log.debug('Deleting template', { action: 'deleteTemplate', templateId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/templates/${templateId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to delete template', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to delete template');
    }

    this.log.info('Template deleted', { action: 'deleteTemplate', templateId });
  }

  /**
   * Copy platform default templates to user's collection.
   */
  async copyDefaultTemplates(options?: CopyDefaultsRequest): Promise<CopyDefaultsResponse> {
    this.log.debug('Copying default templates', { action: 'copyDefaultTemplates', options });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/templates/copy-defaults`, {
      method: 'POST',
      headers,
      body: options ? JSON.stringify(options) : undefined,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to copy default templates', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to copy default templates');
    }

    const data = await response.json();
    this.log.info('Default templates copied', { action: 'copyDefaultTemplates', copied: data.copied });
    return data;
  }

  /**
   * @deprecated Use copyDefaultTemplates instead
   */
  async seedTemplates(): Promise<{ seeded: number; templates: TemplateMetadata[] }> {
    const result = await this.copyDefaultTemplates();
    return { seeded: result.copied, templates: result.templates };
  }

  // ============================================
  // Default Templates (DynamoDB DefaultTemplates table)
  // ============================================

  async listDefaultTemplates(): Promise<DefaultTemplateMetadata[]> {
    this.log.debug('Listing default templates', { action: 'listDefaultTemplates' });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/default-templates`, { method: 'GET', headers });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to list default templates', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list default templates');
    }

    const data = await response.json();
    return data.templates || [];
  }

  async getDefaultTemplate(templateId: string): Promise<DefaultTemplateMetadata> {
    this.log.debug('Getting default template', { action: 'getDefaultTemplate', templateId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/default-templates/${templateId}`, { method: 'GET', headers });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get default template', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get default template');
    }

    const data = await response.json();
    return data.template;
  }

  async createDefaultTemplate(request: SaveDefaultTemplateRequest): Promise<DefaultTemplateMetadata> {
    this.log.debug('Creating default template', { action: 'createDefaultTemplate', name: request.templateName });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/default-templates`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to create default template', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to create default template');
    }

    const data = await response.json();
    return data.template;
  }

  async updateDefaultTemplate(templateId: string, request: Partial<SaveDefaultTemplateRequest>): Promise<DefaultTemplateMetadata> {
    this.log.debug('Updating default template', { action: 'updateDefaultTemplate', templateId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/default-templates/${templateId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to update default template', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to update default template');
    }

    const data = await response.json();
    return data.template;
  }

  async deleteDefaultTemplate(templateId: string): Promise<void> {
    this.log.debug('Deleting default template', { action: 'deleteDefaultTemplate', templateId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/default-templates/${templateId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to delete default template', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to delete default template');
    }

    this.log.info('Default template deleted', { action: 'deleteDefaultTemplate', templateId });
  }
}

// Export singleton instance
export const templatesApi = new TemplatesApiService();

// Export class for testing or custom instances
export { TemplatesApiService };
